// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SettingsByRange.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { compose, withHandlers, withState } from 'recompose';
import { curry, find, get } from 'lodash/fp';
import Constants from 'logic/data/Izenda.SettingsByRangeConstants';
import SettingsByRangeItemFieldModel from 'logic/models/Izenda.SettingsByRangeItemFieldModel';

/**
 * Change threshold type handler.
 * @param props
 * @param thresholdObject
 */
const updateThresholdType = (props, thresholdObject) => {
  const {itemData} = props;
  const thresholdType = itemData.thresholdType = thresholdObject.value;

  if (thresholdType === Constants.THRESHOLD.THRESHOLD_TYPES.DYNAMIC_SUBTOTAL.value) {
    setUpDefaultThresholdSubtotal(props, itemData, null);
  } else if (thresholdType === Constants.THRESHOLD.THRESHOLD_TYPES.DYNAMIC.value) {
    setUpDefaultThresholdField(props, itemData);
  } else {
    setUpDefaultThresholdStatic(props, itemData);
  }
};

/**
 * DYNAMIC_SUBTOTAL threshold subtotal function changed handler.
 */
const updateThresholdFunction = (props: any, fieldModel: SettingsByRangeItemFieldModel, changesData: any) => {
  const { itemData, fieldFunctions } = props;
  // update function (+functions list)
  itemData.functionId = fieldModel.functionId = changesData.value;
  itemData.functions = fieldFunctions;
};

/**
 * DYNAMIC threshold field changed handler.
 * @param props
 * @param fieldModel
 * @param changesData
 */
const updateThresholdField = (props: any, fieldModel: SettingsByRangeItemFieldModel, changesData: any) => {
  const { dataTree, itemData } = props;
  const { fieldId, functionId, functionData, querySourceId, dataType } = changesData;

  const functionName = get('name', functionData); // current dynamic field function
  const fieldInTree = find(f => f.id === fieldId && f.querySourceDetail.id === querySourceId, dataTree);

  // populate model with new values (.fieldName property is empty at this moment, it will
  // be populated later, after inserting into the container)
  fieldModel.populateField(fieldInTree, fieldId, fieldId, functionId, functionName, querySourceId, dataType);
  fieldModel.updateFieldName(fieldInTree.name);
  itemData.fields = [fieldModel];
  itemData.functionData = functionData;
  itemData.functionId = functionId;
};

/**
 * Init default threshold subtotal. Threshold field will be the field which we're configuring.
 * @param {Object} props component props
 * @param {Object} itemData
 * @param {string} functionId
 */
function setUpDefaultThresholdSubtotal(props, itemData, functionId): void {
  const { configField } = props;

  // Create field model based on current config field and init itemData.fields:
  const fieldModel = new SettingsByRangeItemFieldModel();
  fieldModel.populateField(
    configField.field,
    configField.field.fieldId,
    configField.field.fieldId,
    configField.functionInfo.id,
    configField.functionInfo.name,
    configField.field.querySourceId,
    configField.field.dataFieldType
  );
  itemData.fields = [fieldModel];
}

/**
 * Set up default dynamic threshold
 * @param props
 * @param itemData
 */
function setUpDefaultThresholdField(props, itemData) {
  itemData.functionId = null;
  itemData.fields = [new SettingsByRangeItemFieldModel()];
}

/**
 * Set up default static threshold
 * @param props
 * @param itemData
 */
function setUpDefaultThresholdStatic(props, itemData) {
  itemData.functionId = null;
  itemData.fields = [new SettingsByRangeItemFieldModel()];
}

export default compose(
  withState('itemData', 'updateItemData', props => props.itemData),
  withState('fieldFunctions', 'updateFieldFunctions', []),
  withHandlers({
    updateThresholdType: curry(updateThresholdType),
    updateThresholdField: curry(updateThresholdField),
    updateThresholdFunction: curry(updateThresholdFunction)
  })
);



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/CommonSettings/Izenda.SettingsByRangeItemAction.js